/**
 * Media proxy helper.
 *
 * Goal: Never expose WordPress media source URLs to the browser.
 * The browser requests images from our same-origin serverless function,
 * which fetches the bytes from WordPress and returns them with aggressive
 * CDN caching.
 */

// Cloudflare Pages Functions in this repo map /functions/api/* -> /api/*
const DEFAULT_PROXY_ENDPOINT = '/api/speaker-image';

// Dynamic check for react-snap environment
function isPrerender(): boolean {
  if (typeof window === 'undefined') return false;
  
  const isSnap = 
    (window as any).__REACT_SNAP__ === true ||
    !!(window as any).snapSaveState ||
    (navigator.userAgent || '').includes('ReactSnap') ||
    (navigator.userAgent || '').includes('HeadlessChrome');

  return isSnap;
}

/**
 * Build a same-origin URL that will return image bytes for a WP attachment.
 *
 * @param attachmentId WordPress media attachment ID
 * @param size Optional WP size slug (e.g., "thumbnail", "medium", "large", "full")
 */
export function proxiedMediaUrl(attachmentId: number, size?: string): string {
  if (!attachmentId || attachmentId <= 0) return '';

  // During react-snap, return a placeholder to avoid hitting the proxy endpoint
  // which might not be available or might fail during static generation.
  if (isPrerender()) {
    // Use a 1x1 transparent GIF data URI to avoid external network requests
    return 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  }

  // Optional override if you mount functions somewhere else
  const base = process.env.REACT_APP_MEDIA_PROXY_ENDPOINT || DEFAULT_PROXY_ENDPOINT;
  const params = new URLSearchParams();
  params.set('id', String(attachmentId));
  if (size) params.set('size', size);
  return `${base}?${params.toString()}`;
}
